import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardRouteGuard from "./components/DashboardRouteGuard";
import MasterAdminRoute from "./components/MasterAdminRoute";
import {
  installAuthOpenerListener,
  isAuthenticated,
  persistAuthForNewTab,
  requestAuthFromOpener,
  syncAuthFromLocalStorage,
} from "./utils/authStorage";
import { getDefaultDashboardRoute } from "./utils/permissions";
import LoginPage from "./pages/Login/LoginPage";
import DashboardPage from "./pages/Dashboard/Layout/DashboardPage";
import Homepage from "./pages/Dashboard/Home/Homepage";
import UpdateApkPage from "./pages/Dashboard/UpdateApk/UpdateApkPage";
import SubAdminPage from "./pages/Dashboard/Admin/SubAdminPage";
import AdminLogsPage from "./pages/Dashboard/Admin/AdminLogsPage";
import AdminSettingsPage from "./pages/Dashboard/Admin/AdminSettingsPage";
import TradersListPage from "./pages/Dashboard/Traders/TradersListPage";
import BlockedTradersListPage from "./pages/Dashboard/Traders/BlockedTradersListPage";
import UnverifiedTradersListPage from "./pages/Dashboard/Traders/UnverifiedTradersListPage";
import TraderDetailsPage from "./pages/Dashboard/Traders/TraderDetailsPage";
import UserReferralNetworkPage from "./pages/Dashboard/Traders/UserReferralNetworkPage";
import KYCManagementPage from "./pages/Dashboard/KYC/KYCManagementPage";
import CurrencyManagementPage from "./pages/Dashboard/Trading/CurrencyManagementPage";
import CurrencyPairManagementPage from "./pages/Dashboard/Trading/CurrencyPairManagementPage";
import AddCurrencyPairPage from "./pages/Dashboard/Trading/AddCurrencyPairPage";
import CoinListDetailsPage from "./pages/Dashboard/Trading/CoinListDetailsPage";
import AddSpotTradingCoinPage from "./pages/Dashboard/Trading/AddSpotTradingCoinPage";
import SpotMarketTrades from "./pages/Dashboard/Trading/SpotMarketTrades";
import OrderBookPage from "./pages/Dashboard/Trading/OrderBookPage";
import AllOpenOrdersPage from "./pages/Dashboard/Trading/AllOpenOrdersPage";
import FuturesOpenOrdersPage from "./pages/Dashboard/Trading/FuturesOpenOrdersPage";
import FuturesPositionsPage from "./pages/Dashboard/Trading/FuturesPositionsPage";
import FuturesTradeHistoryPage from "./pages/Dashboard/Trading/FuturesTradeHistoryPage";
import FuturesPairPage from "./pages/Dashboard/Trading/FuturesPairPage";
import GlobalWalletTransactionsPage from "./pages/Dashboard/Funds/GlobalWalletTransactionsPage";
import FundsDepositPage from "./pages/Dashboard/Funds/FundsDepositPage";
import DepositSummaryByCurrencyPage from "./pages/Dashboard/Funds/DepositSummaryByCurrencyPage";
import TransferTransactionsPage from "./pages/Dashboard/Funds/TransferTransactionsPage";
import FundsWithdrawalPage from "./pages/Dashboard/Funds/FundsWithdrawalPage";
import TradingCommissionPage from "./pages/Dashboard/Trading/TradingCommissionPage";
import WithdrawalFeesPage from "./pages/Dashboard/Trading/WithdrawalFeesPage";
import ExchangeWalletPage from "./pages/Dashboard/Exchange/ExchangeWalletPage";
import LaunchpadManagementPage from "./pages/Dashboard/Exchange/LaunchpadManagementPage";
import AdminDebitCreditPage from "./pages/Dashboard/Exchange/AdminDebitCreditPage";
import AnnouncementManagementPage from "./pages/Dashboard/Exchange/AnnouncementManagementPage";
import P2POrdersPage from "./pages/Dashboard/P2P/P2POrdersPage";
import P2PAdsPage from "./pages/Dashboard/P2P/P2PAdsPage";
import P2PAdDetailsPage from "./pages/Dashboard/P2P/P2PAdDetailsPage";
import DisputeManagementPage from "./pages/Dashboard/P2P/DisputeManagementPage";
import DisputeDetailsPage from "./pages/Dashboard/P2P/DisputeDetailsPage";
import P2PFiatManagementPage from "./pages/Dashboard/P2P/P2PFiatManagementPage";
import P2PUserPaymentMethodsPage from "./pages/Dashboard/P2P/P2PUserPaymentMethodsPage";
import NotificationManagementPage from "./pages/Dashboard/Notification/NotificationManagementPage";
import BonusManagementPage from "./pages/Dashboard/System/BonusManagementPage";
import ReferralManagementPage from "./pages/Dashboard/System/ReferralManagementPage";
import UplineDownlinePage from "./pages/Dashboard/System/UplineDownlinePage";
import CommissionReportPage from "./pages/Dashboard/System/CommissionReportPage";
import CommissionReportDetailPage from "./pages/Dashboard/System/CommissionReportDetailPage";
import DownlineViewPage from "./pages/Dashboard/System/DownlineViewPage";
import DownlineViewDetailPage from "./pages/Dashboard/System/DownlineViewDetailPage";
import ReferralReleasePlansPage from "./pages/Dashboard/System/ReferralReleasePlansPage";
import RoundReleaseLedgerPage from "./pages/Dashboard/System/RoundReleaseLedgerPage";
import RoundReleaseLedgerDetailPage from "./pages/Dashboard/System/RoundReleaseLedgerDetailPage";
import RoundReleaseReportPage from "./pages/Dashboard/System/RoundReleaseReportPage";
import BrokerageEarnersPage from "./pages/Dashboard/System/BrokerageEarnersPage";
import BrokerageEarnerDetailsPage from "./pages/Dashboard/System/BrokerageEarnerDetailsPage";
import ManageBonusFundsPage from "./pages/Dashboard/System/ManageBonusFundsPage";
import SupportManagementPage from "./pages/Dashboard/Support/SupportManagementPage";
import SupportMessagePage from "./pages/Dashboard/Support/SupportMessagePage";
import PlatformConfigurationPage from "./pages/Dashboard/Platform/PlatformConfigurationPage";
import StakingPackagesPage from "./pages/Dashboard/Staking/StakingPackagesPage";
import AddStakingPackagePage from "./pages/Dashboard/Staking/AddStakingPackagePage";
import EditStakingPackagePage from "./pages/Dashboard/Staking/EditStakingPackagePage";
import StakingStakesPage from "./pages/Dashboard/Staking/StakingStakesPage";
import CancelledStakingPage from "./pages/Dashboard/Staking/CancelledStakingPage";
import StakingFullReportPage from "./pages/Dashboard/Staking/StakingFullReportPage";
import StakingFullReportDetailPage from "./pages/Dashboard/Staking/StakingFullReportDetailPage";
import StakingStatementPage from "./pages/Dashboard/Staking/StakingStatementPage";
import StakingStatementDetailPage from "./pages/Dashboard/Staking/StakingStatementDetailPage";
import StakingSubscribeByAdminPage from "./pages/Dashboard/Staking/StakingSubscribeByAdminPage";
import ZilPricingSettingsPage from "./pages/Dashboard/Settings/ZilPricingSettingsPage";
import SendEmailPage from "./pages/Dashboard/Communication/SendEmailPage";
import CopyTradingManagementPage from "./pages/Dashboard/CopyTrading/CopyTradingManagementPage";
import MasterApplicationsPage from "./pages/Dashboard/CopyTrading/MasterApplicationsPage";
import MasterApplicationDetailPage from "./pages/Dashboard/CopyTrading/MasterApplicationDetailPage";
import "./App.css";

function App() {
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    syncAuthFromLocalStorage();
    persistAuthForNewTab();
    const removeOpenerListener = installAuthOpenerListener();

    const initAuth = async () => {
      if (!isAuthenticated() && window.opener && !window.opener.closed) {
        await requestAuthFromOpener();
      }
      setAuthReady(true);
    };

    initAuth();
    return removeOpenerListener;
  }, []);

  if (!authReady) {
    return null;
  }

  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 2500,
          style: { background: "#2b313c", color: "#e0e0e0", fontSize: "14px" },
        }}
      />
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        >
          <Route element={<DashboardRouteGuard />}>
            <Route index element={<Navigate to={getDefaultDashboardRoute()} replace />} />
            <Route path="homepage" element={<Homepage />} />
            <Route
              path="listsubadmin"
              element={
                <MasterAdminRoute>
                  <SubAdminPage />
                </MasterAdminRoute>
              }
            />
            <Route
              path="admin-logs"
              element={
                <MasterAdminRoute>
                  <AdminLogsPage />
                </MasterAdminRoute>
              }
            />
            <Route
              path="admin-settings"
              element={
                <MasterAdminRoute>
                  <AdminSettingsPage />
                </MasterAdminRoute>
              }
            />
          <Route path="tradelist" element={<TradersListPage />} />
          <Route path="unverified-traders" element={<UnverifiedTradersListPage />} />
          <Route path="blocked-traders" element={<BlockedTradersListPage />} />
          <Route path="trader/:traderId" element={<TraderDetailsPage />} />
          <Route path="trader/:userId/referral-network" element={<UserReferralNetworkPage />} />
          <Route path="copy-trading" element={<CopyTradingManagementPage />} />
          <Route path="copy-trading/master-applications" element={<MasterApplicationsPage />} />
          <Route
            path="copy-trading/master-applications/:applicationId"
            element={<MasterApplicationDetailPage />}
          />
          <Route path="pendingkyc" element={<KYCManagementPage />} />
          <Route path="coinListDetails" element={<CoinListDetailsPage />} />
          <Route path="addSpotTradingCoin" element={<AddSpotTradingCoinPage />} />
          <Route path="currencymanagement" element={<CurrencyManagementPage />} />
          <Route path="addCurrencyPair" element={<AddCurrencyPairPage />} />
          <Route path="currencypair" element={<CurrencyPairManagementPage />} />
          <Route path="spotMarketTrades" element={<SpotMarketTrades />} />
          <Route path="OrderBook" element={<OrderBookPage />} />
          <Route path="allOpenOrders" element={<AllOpenOrdersPage />} />
          <Route path="futuresPair" element={<FuturesPairPage />} />
          <Route path="futuresOpenOrders" element={<FuturesOpenOrdersPage />} />
          <Route path="futuresPositions" element={<FuturesPositionsPage />} />
          <Route path="futuresTradeHistory" element={<FuturesTradeHistoryPage />} />
          <Route path="fundsDManagement" element={<FundsDepositPage />} />
          <Route path="deposit-summary" element={<DepositSummaryByCurrencyPage />} />
          <Route
            path="wallet/gas-fee-transfers"
            element={<GlobalWalletTransactionsPage variant="gas" />}
          />
          <Route
            path="wallet/hot-wallet-transfers"
            element={<GlobalWalletTransactionsPage variant="hot" />}
          />
          <Route path="transferTransactions" element={<TransferTransactionsPage />} />
          <Route path="fundsWithdrawalManagement" element={<FundsWithdrawalPage />} />
          <Route path="TradingCommision" element={<TradingCommissionPage />} />
          <Route path="WithdrawalFees" element={<WithdrawalFeesPage />} />
          <Route path="exchangeWalletManagement" element={<ExchangeWalletPage />} />
          <Route path="LaunchpadManagement" element={<LaunchpadManagementPage />} />
          <Route path="AdminDebitCreditTrans" element={<AdminDebitCreditPage />} />
          <Route path="AnnouncementDetails" element={<AnnouncementManagementPage />} />
          <Route path="p2p-orders" element={<P2POrdersPage />} />
          <Route path="p2p-ads" element={<P2PAdsPage />} />
          <Route path="p2p-ad-details" element={<P2PAdDetailsPage />} />
          <Route path="dispute_management" element={<DisputeManagementPage />} />
          <Route path="dispute_Details" element={<DisputeDetailsPage />} />
          <Route path="p2p-fiat-management" element={<P2PFiatManagementPage />} />
          <Route path="p2p-user-payment-methods" element={<P2PUserPaymentMethodsPage />} />
          <Route path="notification" element={<NotificationManagementPage />} />
          <Route path="bonus-management" element={<BonusManagementPage />} />
          <Route path="referral-management" element={<ReferralManagementPage />} />
          <Route path="upline-downline" element={<UplineDownlinePage />} />
          <Route path="commission-report" element={<CommissionReportPage />} />
          <Route path="commission-report/:userId" element={<CommissionReportDetailPage />} />
          <Route path="referral/downline-view" element={<DownlineViewPage />} />
          <Route path="referral/downline-view/:userId" element={<DownlineViewDetailPage />} />
          <Route path="referral-release-plans" element={<ReferralReleasePlansPage />} />
          <Route path="referral/round-release-report" element={<RoundReleaseReportPage />} />
          <Route path="referral/round-releases" element={<RoundReleaseLedgerPage />} />
          <Route path="referral/round-releases/:userId" element={<RoundReleaseLedgerDetailPage />} />
          <Route path="brokerage" element={<BrokerageEarnersPage />} />
          <Route path="brokerage/earners/:userId" element={<BrokerageEarnerDetailsPage />} />
          <Route path="manage-bonus-funds" element={<ManageBonusFundsPage />} />
          <Route path="support" element={<SupportManagementPage />} />
          <Route path="support-message/:ticketId?" element={<SupportMessagePage />} />
          <Route path="UpdateApk" element={<UpdateApkPage />} />
          <Route path="platform-configuration" element={<PlatformConfigurationPage />} />
          <Route path="staking-packages" element={<StakingPackagesPage />} />
          <Route path="staking/add-package" element={<AddStakingPackagePage />} />
          <Route path="staking/edit-package/:packageId" element={<EditStakingPackagePage />} />
          <Route path="staking/user-stakes" element={<StakingStakesPage />} />
          <Route path="staking/cancelled" element={<CancelledStakingPage />} />
          <Route path="staking/stake-report" element={<StakingFullReportPage />} />
          <Route path="staking/stake-report/:subscriptionId" element={<StakingFullReportDetailPage />} />
          <Route path="staking/statement" element={<StakingStatementPage />} />
          <Route path="staking/statement/:userId" element={<StakingStatementDetailPage />} />
          <Route path="staking/subscribe-by-admin" element={<StakingSubscribeByAdminPage />} />
          <Route path="settings/zil-pricing" element={<ZilPricingSettingsPage />} />
          <Route path="send-email" element={<SendEmailPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
